/**
 * A real detection result: the 29 chapters of the Computer Science textbook, with
 * the page ranges the pipeline actually uses (`igcse-prep/config.json`).
 *
 * Confidence is what a boundary detector would emit per chapter — high for a clean
 * numbered heading, lower where it was ambiguous. Chapters 5 and 17 are marked
 * uncertain so the screen has genuine doubt to surface rather than a happy path.
 *
 * The gap between chapter 4 (ends p39) and chapter 5 (starts p42) is real, not
 * injected: pages 40-41 are front matter for the next unit and belong to no
 * chapter. That is exactly the kind of thing a student must confirm rather than
 * have silently ingested.
 */

import type { SourceDocument } from "../review/types";

export const sampleDocument: SourceDocument = {
  filename: "Computer Science textbook.pdf",
  pageCount: 288,
  chapters: [
    { number: 1, title: "Understanding algorithms", startPage: 12, endPage: 19, confidence: 0.98 },
    { number: 2, title: "Creating algorithms", startPage: 20, endPage: 22, confidence: 0.98 },
    {
      number: 3,
      title: "Sorting and searching algorithms",
      startPage: 23,
      endPage: 30,
      confidence: 0.98,
    },
    {
      number: 4,
      title: "Decomposition and abstraction",
      startPage: 31,
      endPage: 39,
      confidence: 0.98,
    },
    { number: 5, title: "Develop code", startPage: 42, endPage: 58, confidence: 0.61 },
    {
      number: 6,
      title: "Making programs easy to read",
      startPage: 59,
      endPage: 61,
      confidence: 0.98,
    },
    { number: 7, title: "Strings", startPage: 62, endPage: 69, confidence: 0.98 },
    { number: 8, title: "Data structures", startPage: 70, endPage: 77, confidence: 0.98 },
    { number: 9, title: "Input/output", startPage: 78, endPage: 89, confidence: 0.98 },
    { number: 10, title: "Subprograms", startPage: 90, endPage: 99, confidence: 0.98 },
    { number: 11, title: "Testing and evaluation", startPage: 100, endPage: 115, confidence: 0.98 },
    { number: 12, title: "Binary", startPage: 118, endPage: 132, confidence: 0.88 },
    { number: 13, title: "Data representation", startPage: 133, endPage: 143, confidence: 0.98 },
    {
      number: 14,
      title: "Data storage and compression",
      startPage: 144,
      endPage: 151,
      confidence: 0.98,
    },
    { number: 15, title: "Encryption", startPage: 152, endPage: 165, confidence: 0.98 },
    {
      number: 16,
      title: "Machines and computational models",
      startPage: 168,
      endPage: 172,
      confidence: 0.98,
    },
    { number: 17, title: "Hardware", startPage: 173, endPage: 188, confidence: 0.61 },
    { number: 18, title: "Logic", startPage: 189, endPage: 194, confidence: 0.98 },
    { number: 19, title: "Software", startPage: 195, endPage: 201, confidence: 0.98 },
    { number: 20, title: "Programming languages", startPage: 202, endPage: 207, confidence: 0.98 },
    { number: 21, title: "Networks", startPage: 210, endPage: 228, confidence: 0.98 },
    { number: 22, title: "Network security", startPage: 229, endPage: 240, confidence: 0.98 },
    {
      number: 23,
      title: "The internet and the world wide web",
      startPage: 241,
      endPage: 249,
      confidence: 0.88,
    },
    {
      number: 24,
      title: "Computing and the environmental impact of technology",
      startPage: 252,
      endPage: 257,
      confidence: 0.98,
    },
    { number: 25, title: "Privacy", startPage: 258, endPage: 262, confidence: 0.98 },
    { number: 26, title: "Digital inclusion", startPage: 263, endPage: 265, confidence: 0.98 },
    { number: 27, title: "Professionalism", startPage: 266, endPage: 267, confidence: 0.98 },
    {
      number: 28,
      title: "Computing and the legal impact of technology",
      startPage: 268,
      endPage: 271,
      confidence: 0.98,
    },
    {
      number: 29,
      title: "Current and emerging trends",
      startPage: 272,
      endPage: 279,
      confidence: 0.98,
    },
  ],
  evidence: {
    method: "contents",
    offset: 8,
    samples: [
      { pdfPage: 12, printed: 4, agrees: true },
      { pdfPage: 118, printed: 110, agrees: true },
      { pdfPage: 252, printed: 244, agrees: true },
    ],
    contentsPages: [4, 5],
    reason: null,
  },
  gaps: [
    { startPage: 1, endPage: 11, label: "Front matter" },
    { startPage: 40, endPage: 41, label: "Unit 2 opener" },
    { startPage: 280, endPage: 288, label: "Answers" },
  ],
};
